import { WORKSPACE_SHORTCUT_ORDER, WORKSPACE_SHORTCUTS } from "../workspaceShortcuts";

export const SETTINGS_SECTIONS = {
  general: {
    label: "General",
    path: "/settings/general",
  },
  appearance: {
    label: "Appearance",
    path: "/settings/appearance",
  },
  keybindings: {
    label: "Keybindings",
    path: "/settings/keybindings",
  },
  connections: {
    label: "Connections",
    path: "/settings/connections",
  },
} as const;

export type SettingsSection = keyof typeof SETTINGS_SECTIONS;

export const SETTINGS_SECTION_ORDER = [
  "general",
  "appearance",
  "keybindings",
  "connections",
] as const satisfies readonly SettingsSection[];

export interface SettingsSearchItem {
  readonly id: string;
  readonly title: string;
  readonly section: SettingsSection;
  readonly desktopOnly?: true;
}

const SETTINGS_SEARCH_ITEMS = [
  { id: "desktop-runtime", title: "Desktop runtime", section: "general" },
  { id: "workspace-layout", title: "Workspace layout", section: "general" },
  { id: "browser-viewport", title: "Browser viewport", section: "general" },
  { id: "color-scheme", title: "Color scheme", section: "appearance" },
  { id: "accent-theme", title: "Accent theme", section: "appearance" },
  { id: "glass-opacity", title: "Glass opacity", section: "appearance" },
  { id: "time-format", title: "Time format", section: "appearance" },
  { id: "interface-font", title: "Interface font", section: "appearance" },
  { id: "editorial-font", title: "Editorial font", section: "appearance" },
  { id: "code-font", title: "Code font", section: "appearance" },
  {
    id: "terminal-text-size",
    title: "Terminal text size",
    section: "appearance",
    desktopOnly: true,
  },
  {
    id: "file-preview-word-wrap",
    title: "File preview word wrap",
    section: "appearance",
  },
  {
    id: "file-preview-markdown-mode",
    title: "Markdown preview",
    section: "appearance",
  },
  { id: "motion", title: "Reduced motion", section: "appearance" },
  ...WORKSPACE_SHORTCUT_ORDER.map((id) => ({
    id,
    title: WORKSPACE_SHORTCUTS[id].title,
    section: "keybindings" as const,
  })),
  { id: "workspace-services", title: "Workspace services", section: "connections" },
] as const satisfies readonly SettingsSearchItem[];

function normalizeSearchText(value: string): string {
  return value
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/gu, "")
    .toLocaleLowerCase()
    .replace(/\s+/gu, " ")
    .trim();
}

export function searchSettings(
  query: string,
  desktop: boolean = false,
): readonly SettingsSearchItem[] {
  const normalizedQuery = normalizeSearchText(query);
  if (normalizedQuery.length === 0) return [];

  return SETTINGS_SEARCH_ITEMS.filter(
    (item) =>
      (desktop || !("desktopOnly" in item)) &&
      normalizeSearchText(`${item.title} ${SETTINGS_SECTIONS[item.section].label}`).includes(
        normalizedQuery,
      ),
  );
}
